import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { ChatMessageType, UserOnEntity } from "@/graphql/__generated__/graphql";
import { useCreateChatMessageMutationHook } from "@/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import React, { useState, useRef, RefObject } from "react";
import { onInvalid } from "@/utils/form";
import { filterUsersByMention, handleMentionDetection, transformMessageForSubmission } from "@/components/shared/mentions/utils";
import { ChatMentionsPopup } from "@/components/shared/mentions";
import { CircleLoader } from "@/components/shared/loaders";
import { t } from "i18next";
import { useTenderPermissionsData } from "@/hooks/cache";

interface Props {
  tenderID: string | undefined;
  tenderUsers: UserOnEntity[];
};

const ChatSchema = z.object({
  message: z.string().trim().nonempty(t("schemas:form.message")),
});

/**
 * Chat input component for sending messages
 * @param tenderID - string
 * @returns
 */
function ChatInput({ tenderID, tenderUsers }: Props) {

  // Hooks
  const { t } = useTranslation(["components"]);
  const { createChatMessageMutation, loading } = useCreateChatMessageMutationHook();
  const { canUseChatMessage } = useTenderPermissionsData(Number(tenderID));

  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Filter tender users based on the current mention query.
  const filteredUsers = filterUsersByMention(mentionQuery, tenderUsers);

  const form = useForm<z.infer<typeof ChatSchema>>({
    resolver: zodResolver(ChatSchema)
  });

  function onSubmit(data: z.infer<typeof ChatSchema>) {
    // Transform the message to include token mentions for the backend
    const finalMessage = transformMessageForSubmission(data.message, tenderUsers);

    createChatMessageMutation({
      variables: {
        input: {
          entityId: Number(tenderID),
          type: ChatMessageType.Tender,
          text: finalMessage
        }
      }
    }).then(() => {
      form.setValue("message", "");
    });
  }

  const handleSubmitOnClick = form.handleSubmit(onSubmit, onInvalid);

  /**
   * Handles the keydown event on the input field.
   *
   * When the "Enter" key is pressed and the form is valid, this function prevents the default action.
   *
   * @param {React.KeyboardEvent<HTMLTextAreaElement>} e - The keyboard event triggered by the input field.
   * @param {UseFormReturn<any>} form - The form object.
   * @returns {void}
   */
  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, form: UseFormReturn<any>): void => {
    if (e.key === "Enter") {
      if (e.shiftKey) {
        // Allow default behavior for Shift+Enter (new line)
        return;
      }

      e.preventDefault(); // Always prevent default for Enter without shift

      // Get the current message and check if it's valid
      const message = form.getValues("message");
      if (message && message.trim() !== "") {
        // Only submit if there's actual content
        form.handleSubmit(onSubmit, onInvalid)();
      }
    }
  };

  return (
    <Form {...form}>
      <form>
        <div className="flex flex-col items-end justify-between gap-2 pr-4 relative">
          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Textarea
                    ref={textareaRef}
                    placeholder={t("chat.placeholder")}
                    className="w-full min-h-[40px] resize-none ml-1"
                    onChange={(e) => {
                      field.onChange(e);
                      handleMentionDetection(e, setMentionQuery);
                    }}
                    onKeyDown={(e) => handleOnKeyDown(e, form)}
                    value={field.value || ""}
                    rows={3}
                    autoComplete="off"
                    disabled={!canUseChatMessage}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          {mentionQuery !== null && filteredUsers.length > 0 && (
            <ChatMentionsPopup
              filteredUsers={filteredUsers}
              inputRef={textareaRef as RefObject<HTMLInputElement | HTMLTextAreaElement>}
              form={form}
              formField="message"
              setMentionQuery={setMentionQuery}
            />
          )}

          <Button
            type="submit"
            onClick={handleSubmitOnClick}
            // Disable button when loading is true or message input is empty
            disabled={loading || (form.watch("message") === "") || !canUseChatMessage}
            size="sm"
            className="gap-2"
          >
            <CircleLoader visible={loading} height={16} width={16} strokeWidth={2} />
            {t("chat.send")}
          </Button>
        </div>
      </form>
    </Form>
  );
}

export default ChatInput;
